import axios from "axios";
import Config from "../_config/config";

/**
 * Service for Study Resource Submissions
 */
class StudyResourceSubmissionService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.nodeSiteUrl}v1/study-resource/submission`;
  }

  /**
   * Get auth headers with Bearer token
   */
  private getAuthHeaders(): any {
    const headers: any = {
      "Content-Type": "application/json",
    };

    const currentUser = localStorage.getItem("currentUser");
    if (currentUser && currentUser !== "undefined") {
      try {
        const user = JSON.parse(currentUser);
        const token = user.token;
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error("Failed to parse currentUser from localStorage:", error);
      }
    }

    return headers;
  }

  /**
   * Get submission by ID
   * Returns both submission data and rubric (if available)
   */
  async getSubmissionById(submissionId: number): Promise<{ submission: any; rubric: any }> {
    try {
      const url = `${this.baseUrl}/${submissionId}`;
      const response = await axios.get(url, {
        headers: this.getAuthHeaders(),
      });

      const responseData = response.data as any;
      return {
        submission: responseData.data || responseData,
        rubric: responseData.rubric || null
      };
    } catch (error: any) {
      console.error("[StudyResourceSubmissionService] Error fetching submission:", error);
      throw error;
    }
  }

  /**
   * Reset all grade information for a submission
   * Clears scores, feedback, and resets status to SUBMITTED
   */
  async resetGrade(submissionId: number): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const url = `${this.baseUrl}/${submissionId}/reset-grade`;
      const response = await axios.patch(url, {}, {
        headers: this.getAuthHeaders(),
      });

      const responseData: any = response.data;
      return {
        success: responseData.success !== undefined ? responseData.success : true,
        message: responseData.message || "Grade reset successfully",
        data: responseData.data
      };
    } catch (error: any) {
      console.error("[StudyResourceSubmissionService] Error resetting grade:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to reset grade"
      };
    }
  }

  /**
   * Clear submission data selectively
   * @param submissionId - The submission ID
   * @param options - What to clear { clearSubmissionData, clearSubmissionContextData, resetStatus }
   */
  async clearSubmissionData(
    submissionId: number,
    options: {
      clearSubmissionData?: boolean;
      clearSubmissionContextData?: boolean;
      resetStatus?: boolean;
    }
  ): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const url = `${this.baseUrl}/${submissionId}/clear`;
      const response = await axios.patch(url, options, {
        headers: this.getAuthHeaders(),
      });

      const responseData: any = response.data;
      return {
        success: responseData.success !== undefined ? responseData.success : true,
        message: responseData.message || "Data cleared successfully",
        data: responseData.data
      };
    } catch (error: any) {
      console.error("[StudyResourceSubmissionService] Error clearing submission data:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to clear submission data"
      };
    }
  }

  /**
   * Delete a submission permanently (hard delete)
   * WARNING: This cannot be undone
   */
  async deleteSubmission(submissionId: number): Promise<{ success: boolean; message: string; deletedId?: number }> {
    try {
      const url = `${this.baseUrl}/${submissionId}`;
      const response = await axios.delete(url, {
        headers: this.getAuthHeaders(),
      });

      const responseData: any = response.data;
      return {
        success: responseData.success !== undefined ? responseData.success : true,
        message: responseData.message || "Submission deleted successfully",
        deletedId: responseData.deletedId
      };
    } catch (error: any) {
      console.error("[StudyResourceSubmissionService] Error deleting submission:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to delete submission"
      };
    }
  }

  /**
   * Reset workspace session for a student
   * Deletes the ai_workspace_session record so student can start fresh
   */
  async resetWorkspaceSession(
    resourceId: number,
    userId: number
  ): Promise<{ success: boolean; message: string; deletedCount?: number }> {
    try {
      const url = `${Config.nodeSiteUrl}v1/ai-workspace/session/resource/${resourceId}/user/${userId}`;
      const response = await axios.delete(url, {
        headers: this.getAuthHeaders(),
      });

      const responseData: any = response.data;
      return {
        success: responseData.success !== undefined ? responseData.success : true,
        message: responseData.message || "Session reset successfully",
        deletedCount: responseData.deletedCount
      };
    } catch (error: any) {
      console.error("[StudyResourceSubmissionService] Error resetting workspace session:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to reset workspace session"
      };
    }
  }
}

export default new StudyResourceSubmissionService();
